import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2, Plus, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { cn } from '@orisini/ui/lib/utils'
import { useAuth } from '@orisini/ui/context/AuthContext'
import {
  accountsApi,
  DAYS,
  minuteToTime,
  timeToMinute,
  type LatePolicy,
  type Slot,
  type Workspace,
} from '../lib/accounts'

/** Urutan tampil Senin–Minggu; `dow` tetap 0 = Minggu seperti di database. */
const URUT_HARI = [1, 2, 3, 4, 5, 6, 0]

const LATE: { value: LatePolicy; label: string }[] = [
  { value: 'post_late', label: 'Post it as soon as possible' },
  { value: 'requeue', label: 'Move it to the next free time' },
  { value: 'skip', label: 'Skip it' },
]

/** Slot yang belum tersimpan tidak punya id; kuncinya hari + menit. */
type DraftSlot = Omit<Slot, 'id' | 'enabled'>
const kunci = (s: { dow: number; minuteOfDay: number }) => `${s.dow}:${s.minuteOfDay}`

/**
 * Jadwal posting akun: zona waktu, waktu posting mingguan, dan jarak antar post.
 *
 * Waktu posting disimpan sebagai satu pekan utuh (`slots/bulk`). Slot lama
 * dikirim balik LENGKAP — board (Pinterest) dan format (Instagram) per slot
 * ikut, karena simpan-massal menimpa field yang tidak dikirim.
 */
export function PublishingTab({ workspace: w, onChanged }: { workspace: Workspace; onChanged: () => Promise<void> }) {
  const a = w.account
  if (!w.productCode) {
    return (
      <p className="text-sm text-muted-foreground">
        Posting times belong to a social account, and this project has none.{' '}
        <Link to={`/projects/${w.id}/connection`} className="text-foreground underline-offset-4 hover:underline">Connect an account</Link>{' '}
        to schedule posts, or download content from the Content Bank.
      </p>
    )
  }
  if (!a) {
    return <p className="text-sm text-muted-foreground">This account is no longer connected, so it has no schedule.</p>
  }
  return (
    <div className="space-y-6">
      <PostingTimes workspaceId={w.id} />
      <Pacing workspace={w} onChanged={onChanged} />
    </div>
  )
}

function PostingTimes({ workspaceId }: { workspaceId: string }) {
  const [saved, setSaved] = useState<DraftSlot[] | null>(null)
  const [slots, setSlots] = useState<DraftSlot[]>([])
  const [timezone, setTimezone] = useState('')
  const [time, setTime] = useState('09:00')
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    accountsApi
      .slots(workspaceId)
      .then((r) => {
        const rows = r.slots.map(({ id: _id, enabled: _e, ...rest }) => rest)
        setSaved(rows)
        setSlots(rows)
        setTimezone(r.timezone)
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : "Couldn't load posting times"))
  }, [workspaceId])

  const dirty = useMemo(() => {
    if (!saved) return false
    const a = new Set(saved.map(kunci))
    const b = new Set(slots.map(kunci))
    return a.size !== b.size || [...a].some((k) => !b.has(k))
  }, [saved, slots])

  const tambah = () => {
    const minute = timeToMinute(time)
    if (minute === null) return toast.error('Enter a time like 07:30')
    if (!days.length) return toast.error('Pick at least one day')
    const ada = new Set(slots.map(kunci))
    const baru = days
      .map((dow) => ({ dow, minuteOfDay: minute, label: null }))
      .filter((s) => !ada.has(kunci(s)))
    if (!baru.length) return toast.info(`${minuteToTime(minute)} is already on those days`)
    setSlots([...slots, ...baru])
  }

  const simpan = async () => {
    setSaving(true)
    try {
      const r = await accountsApi.saveSlots(workspaceId, slots)
      const rows = r.slots.map(({ id: _id, enabled: _e, ...rest }) => rest)
      setSaved(rows)
      setSlots(rows)
      toast.success('Posting times saved. The queue has been rescheduled.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save posting times")
    } finally {
      setSaving(false)
    }
  }

  if (!saved) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading posting times…
      </div>
    )
  }

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="font-medium">Posting times</h2>
        <p className="text-sm text-muted-foreground">
          Queued posts go out at these times, in order. Times are in {timezone || 'the account time zone'}.
        </p>
      </div>

      <div className="divide-y divide-border rounded-md border border-border">
        {URUT_HARI.map((dow) => {
          const hari = slots.filter((s) => s.dow === dow).sort((x, y) => x.minuteOfDay - y.minuteOfDay)
          return (
            <div key={dow} className="flex items-center gap-3 px-3 py-2">
              <span className="w-10 shrink-0 text-sm text-muted-foreground">{DAYS[dow]}</span>
              <div className="flex min-h-7 flex-1 flex-wrap gap-1.5">
                {hari.length === 0 && <span className="self-center text-xs text-muted-foreground/60">No posts</span>}
                {hari.map((s) => (
                  <span key={kunci(s)} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm tabular-nums">
                    {minuteToTime(s.minuteOfDay)}
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${DAYS[dow]} ${minuteToTime(s.minuteOfDay)}`}
                      onClick={() => setSlots(slots.filter((x) => kunci(x) !== kunci(s)))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="slot-time">Time</Label>
          <Input id="slot-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-32" />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Days</span>
          <div className="flex gap-1">
            {URUT_HARI.map((dow) => (
              <button
                key={dow}
                type="button"
                aria-pressed={days.includes(dow)}
                onClick={() => setDays(days.includes(dow) ? days.filter((d) => d !== dow) : [...days, dow])}
                className={cn(
                  'h-10 w-11 rounded-md border text-xs',
                  days.includes(dow) ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground',
                )}
              >
                {DAYS[dow]}
              </button>
            ))}
          </div>
        </div>
        <Button type="button" variant="outline" onClick={tambah}>
          <Plus /> Add time
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={simpan} disabled={!dirty || saving}>
          {saving && <Loader2 className="animate-spin" />}
          Save posting times
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setSlots(saved)} disabled={saving}>
            Discard changes
          </Button>
        )}
      </div>
    </Card>
  )
}

function Pacing({ workspace: w, onChanged }: { workspace: Workspace; onChanged: () => Promise<void> }) {
  const a = w.account!
  const { me } = useAuth()
  const zones = useMemo(() => {
    try {
      return Intl.supportedValuesOf('timeZone')
    } catch {
      return [a.timezone ?? 'UTC']
    }
  }, [a.timezone])
  const [form, setForm] = useState({
    timezone: a.timezone ?? '',
    minGapMinutes: String(a.minGapMinutes),
    maxPostsPerHour: String(a.maxPostsPerHour),
    latePolicy: a.latePolicy,
    lateGraceMinutes: String(a.lateGraceMinutes),
  })
  const [saving, setSaving] = useState(false)

  const simpan = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await accountsApi.updateAccount(w.id, {
        timezone: form.timezone || null,
        minGapMinutes: Number(form.minGapMinutes),
        maxPostsPerHour: Number(form.maxPostsPerHour),
        latePolicy: form.latePolicy,
        lateGraceMinutes: Number(form.lateGraceMinutes),
      })
      toast.success('Publishing settings saved')
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save settings")
    } finally {
      setSaving(false)
    }
  }

  const selectCls =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Card className="p-5">
      <form onSubmit={simpan} className="space-y-4">
        <div>
          <h2 className="font-medium">Pacing</h2>
          <p className="text-sm text-muted-foreground">How fast this account is allowed to post, and what happens when a post is late.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tz">Time zone</Label>
            <select id="tz" className={selectCls} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              <option value="">Same as my profile{me?.user.timezone ? ` (${me.user.timezone})` : ''}</option>
              {zones.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gap">Minimum gap between posts (minutes)</Label>
            <Input id="gap" type="number" min={1} max={720} value={form.minGapMinutes} onChange={(e) => setForm({ ...form, minGapMinutes: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="perhour">Maximum posts per hour</Label>
            <Input id="perhour" type="number" min={1} max={25} value={form.maxPostsPerHour} onChange={(e) => setForm({ ...form, maxPostsPerHour: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="late">If a post is late</Label>
            <select id="late" className={selectCls} value={form.latePolicy} onChange={(e) => setForm({ ...form, latePolicy: e.target.value as LatePolicy })}>
              {LATE.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="grace">Late by more than (minutes)</Label>
            <Input id="grace" type="number" min={0} max={10080} value={form.lateGraceMinutes} onChange={(e) => setForm({ ...form, lateGraceMinutes: e.target.value })} />
            <p className="text-xs text-muted-foreground">Posts that are only a little late always go out.</p>
          </div>
        </div>

        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="animate-spin" />}
          Save settings
        </Button>
      </form>
    </Card>
  )
}
